import { Component, inject, input, OnInit, signal } from "@angular/core";
import { NgbAccordionModule } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { TranslateTokenPipe } from "../../../../../../../core/pipes";
import { AdaaHelper } from "../../../../../../../core/utils";
import { Constants } from "../../../../../../constants/constants";
import { Language } from "../../../../../../constants/enums";
import { BenchmarkModelType, BenchmarkValueType, PropTypeModelType } from "../../../../../../models";
import { LanguageService, PropertiesService } from "../../../../../../services";
import { AttachmentDiffViewComponent } from "../attachment-diff-view/attachment-diff-view";

const comparedFields = ["nameAE", "nameEN", "benchType", "benchSource"] as const;

type ComparedField = (typeof comparedFields)[number];

type BenchmarkDiffType = {
  hasDiff: boolean;
  new?: BenchmarkModelType;
  old?: BenchmarkModelType;
  lovs: { new?: BenchmarkValueType; old?: BenchmarkValueType }[];
  diffs: Record<ComparedField, boolean>;
};

@Component({
  selector: "adaa-benchmark-diff-view",
  imports: [TranslatePipe, TranslateTokenPipe, AttachmentDiffViewComponent, NgbAccordionModule],
  templateUrl: "./benchmark-diff-view.html",
  styleUrls: ["../styles.scss", "./benchmark-diff-view.scss"],
})
export class BenchmarkDiffViewComponent implements OnInit {
  private _propertiesService = inject(PropertiesService);
  readonly languageService = inject(LanguageService);
  benchmarks = signal<BenchmarkDiffType[]>([]);

  showOnlyNew = input.required<boolean>();
  newData = input<BenchmarkModelType[]>([]);
  oldData = input<BenchmarkModelType[]>([]);
  benchTypes = signal<PropTypeModelType[]>([]);
  Constants = Constants;
  Language = Language;

  public benchType(value: number) {
    const type = this.benchTypes().find((type) => type.id === value);
    return type ? AdaaHelper.getItemValueByToken(type, "name") : "";
  }

  public ngOnInit(): void {
    this._propertiesService.getPropById(Constants.CONSTANT_BENCHTYPE).subscribe({
      next: (response) => {
        this.benchTypes.set(response.responseData);
      },
    });
    this._processbenchmarks();
  }

  private _processbenchmarks() {
    const result: BenchmarkDiffType[] = [];

    const newData = this.newData() ?? [];
    const oldData = this.oldData() ?? [];

    for (const newItem of newData) {
      const oldItem = oldData.find((data) => data.id === newItem.id);
      const hasDiff = oldItem ? newItem.status !== oldItem.status : false;

      result.push({
        new: { ...newItem },
        ...(oldItem && { old: { ...oldItem } }),
        hasDiff,
        lovs: this.mergeNewAndOld(newItem.kpiBenchMarkLovs, oldItem?.kpiBenchMarkLovs),
        diffs: this._fieldDiffs(newItem, oldItem),
      });
    }

    for (const oldItem of oldData) {
      const newItem = newData.find((data) => data.id === oldItem.id);
      if (!newItem) {
        result.push({
          old: { ...oldItem },
          hasDiff: true,
          lovs: this.mergeNewAndOld(undefined, oldItem.kpiBenchMarkLovs),
          diffs: this._fieldDiffs(undefined, oldItem),
        });
      }
    }

    this.benchmarks.set(result);
  }

  public mergeNewAndOld(
    newData: BenchmarkValueType[] | undefined,
    oldData: BenchmarkValueType[] | undefined
  ): { new?: BenchmarkValueType; old?: BenchmarkValueType }[] {
    const mergedMap = new Map<string, { new?: BenchmarkValueType; old?: BenchmarkValueType }>();

    for (const newItem of newData ?? []) {
      mergedMap.set(this._lovKey(newItem), { new: newItem });
    }

    for (const oldItem of oldData ?? []) {
      const key = this._lovKey(oldItem);
      const entry = mergedMap.get(key);

      if (entry) {
        entry.old = oldItem;
      } else {
        mergedMap.set(key, { old: oldItem });
      }
    }

    return Array.from(mergedMap.values());
  }

  private _fieldDiffs(newItem?: BenchmarkModelType, oldItem?: BenchmarkModelType): Record<ComparedField, boolean> {
    const diffs = {} as Record<ComparedField, boolean>;
    const statusChanged = newItem?.status !== oldItem?.status;

    for (const field of comparedFields) {
      diffs[field] = statusChanged || newItem?.[field] !== oldItem?.[field];
    }

    return diffs;
  }

  private _lovKey(lov: BenchmarkValueType): string {
    return lov.id === null || lov.id === undefined ? `${lov.countryId}-${lov.year}` : `${lov.id}`;
  }
}
